/**
 * What the counters have asked for, waiting on somebody who may decide it.
 *
 * Three kinds of request land here: a fee outside the approved prices, a job
 * dated before today, and an electronic payment nobody has checked yet. Until
 * one is decided its delivery is recorded but not booked, so this screen is
 * where money that is stuck becomes money that is counted — or refused, with
 * the reason on the record.
 *
 * The server shows each person only the kinds they may decide, and refuses a
 * decision on somebody's own request. This screen does not second-guess it;
 * it says what the server said.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { DeliveryApproval, DeliveryApprovalKind } from '@cenox/shared';
import { ApiError, api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { date, dateTime, money } from '../../lib/format';
import { Button, Card, CardHeader, EmptyState, ErrorState, Input, Spinner } from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';

const KIND_LABELS: Record<DeliveryApprovalKind, string> = {
  price: 'Price exception',
  backdate: 'Earlier date',
  payment: 'Payment verification',
};

interface DuplicatePair {
  a: string;
  aReference: string;
  b: string;
  bReference: string;
  customerPhone: string;
  fee: string;
  /** Why the pair was flagged, and each side as recorded. Optional for an older API. */
  reason?: 'same_reference' | 'same_customer_fee_day';
  first?: DuplicateSide;
  second?: DuplicateSide;
}

interface DuplicateSide {
  id: string;
  externalRef: string;
  dispatchRef: string | null;
  originSource: string;
  date: string;
  deliveryStatus: string | null;
  paymentStatus: string | null;
  deliveryType: string | null;
}

const ORIGIN_WORD: Record<string, string> = {
  whatsapp: 'WhatsApp', customer_app: 'App', shop: 'Shop', manual: 'Counter', app: 'App',
};

function describeSide(side: DuplicateSide | undefined): string {
  if (!side) return '';
  return [
    ORIGIN_WORD[side.originSource] ?? side.originSource,
    side.dispatchRef && side.dispatchRef !== side.externalRef ? side.dispatchRef : null,
    side.deliveryStatus,
    side.paymentStatus,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** What was asked for, in the words the counter would use. */
function describeRequest(r: DeliveryApproval, currency: string, timezone: string): string {
  const asked = r.requested;
  if (r.kind === 'price') return `Fee ${money(String(asked['fee'] ?? ''), currency)}`;
  if (r.kind === 'backdate') return `Dated ${date(String(asked['occurredAt'] ?? ''), timezone)}`;
  const method = typeof asked['method'] === 'string' ? asked['method'] : 'Payment';
  return asked['proofAttachmentId'] ? `${method}, proof attached` : method;
}

export function ApprovalsPage(): ReactNode {
  const { business, can } = useAuth();
  const mayDecide = can('deliveries:approve_exceptions') || can('deliveries:verify_payment');

  if (!mayDecide) {
    return (
      <>
        <PageHeader title="Delivery approvals" />
        <Card>
          <EmptyState
            title="Nothing to decide here"
            description="Approving delivery exceptions and payments is for the owner or a manager."
          />
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Delivery approvals"
        description="Fees, dates and payments waiting on a decision. Nothing here is booked until it is approved."
      />
      <PendingRequests />
      {can('deliveries:approve_exceptions') && business && <FeeMismatches />}
      {can('deliveries:approve_exceptions') && business && <PossibleDuplicates />}
    </>
  );
}

function PendingRequests(): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();
  const currency = business?.currencyCode ?? 'NGN';
  const timezone = business?.timezone ?? 'UTC';

  const query = useQuery({
    queryKey: ['delivery-approvals', business?.businessId],
    queryFn: () => api.get<{ data: DeliveryApproval[] }>(path('/deliveries/approvals?status=pending')),
    enabled: Boolean(business),
  });

  const rows = query.data?.data ?? [];

  return (
    <Card>
      <CardHeader title="Waiting for a decision" />
      {query.isLoading && <Spinner label="Loading approvals" />}
      {query.isError && <ErrorState message="Could not load approvals." />}
      {query.data && rows.length === 0 && (
        <EmptyState title="Nothing waiting" description="Every request has been decided." />
      )}
      {rows.length > 0 && (
        <ul className="divide-y divide-slate-100">
          {rows.map((r) => (
            <ApprovalRow
              key={r.id}
              request={r}
              asked={describeRequest(r, currency, timezone)}
              when={dateTime(r.requestedAt, timezone)}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

function ApprovalRow({
  request: r,
  asked,
  when,
}: {
  request: DeliveryApproval;
  asked: string;
  when: string;
}): ReactNode {
  const path = useBusinessPath();
  const queryClient = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const decide = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') =>
      api.post(path(`/deliveries/approvals/${r.id}/decision`), {
        decision,
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['delivery-approvals'] });
      void queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      void queryClient.invalidateQueries({ queryKey: ['delivery'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not record that decision.'),
  });

  return (
    <li className="space-y-2 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-sm">
          <span className="font-medium text-slate-900">{KIND_LABELS[r.kind]}</span>
          {' · '}
          <Link
            to={`/deliveries/${r.deliveryId}`}
            className="font-mono text-xs text-indigo-700 hover:underline"
          >
            {r.deliveryReference}
          </Link>
          {r.branchName && <span className="text-slate-500"> · {r.branchName}</span>}
        </div>
        <span className="text-xs text-slate-500">
          {r.requestedByName ?? 'Someone'} · {when}
        </span>
      </div>
      <p className="text-sm text-slate-900">{asked}</p>
      {r.reason && <p className="text-sm text-slate-600">“{r.reason}”</p>}

      {error && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {rejecting ? (
        <div className="space-y-2">
          <Input
            label="Why is it rejected?"
            required
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="flex gap-2">
            <Button
              variant="danger"
              onClick={() => decide.mutate('rejected')}
              loading={decide.isPending}
              disabled={note.trim().length === 0}
            >
              Reject
            </Button>
            <Button variant="ghost" onClick={() => setRejecting(false)}>
              Back
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button onClick={() => decide.mutate('approved')} loading={decide.isPending}>
            Approve
          </Button>
          <Button variant="secondary" onClick={() => setRejecting(true)}>
            Reject
          </Button>
        </div>
      )}
    </li>
  );
}

/**
 * Pairs that look like one job entered twice: same customer, same fee, within
 * twelve hours. Marking one a duplicate keeps both records and stops the
 * second counting, reversing anything it already booked.
 */
function PossibleDuplicates(): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();
  const currency = business?.currencyCode ?? 'NGN';

  const query = useQuery({
    queryKey: ['delivery-duplicates', business?.businessId],
    queryFn: () => api.get<{ data: DuplicatePair[] }>(path('/deliveries/possible-duplicates')),
    enabled: Boolean(business),
  });
  const pairs = query.data?.data ?? [];

  return (
    <Card className="mt-4">
      <CardHeader
        title="Possible duplicates"
        subtitle="Same order number, or the same customer and fee close together. Nothing is deleted: marking one a duplicate keeps it on record and reverses only what it booked."
      />
      {query.isLoading && <Spinner label="Looking for duplicates" />}
      {query.isError && <ErrorState message="Could not check for duplicates." />}
      {query.data && pairs.length === 0 && (
        <EmptyState title="No likely duplicates" description="Nothing looks entered twice." />
      )}
      {pairs.length > 0 && (
        <ul className="divide-y divide-slate-100">
          {pairs.map((p) => (
            <DuplicateRow key={`${p.a}-${p.b}`} pair={p} fee={money(p.fee, currency)} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function DuplicateRow({ pair: p, fee }: { pair: DuplicatePair; fee: string }): ReactNode {
  const path = useBusinessPath();
  const queryClient = useQueryClient();
  /** Which record stops counting — the reviewer chooses; the other is kept as the job. */
  const [drop, setDrop] = useState<'a' | 'b' | null>(null);
  const [dismissing, setDismissing] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['delivery-duplicates'] });
    void queryClient.invalidateQueries({ queryKey: ['deliveries'] });
    void queryClient.invalidateQueries({ queryKey: ['delivery'] });
  };
  const supersede = useMutation({
    mutationFn: () => {
      const [dup, keep] = drop === 'a' ? [p.a, p.b] : [p.b, p.a];
      return api.post(path(`/deliveries/${dup}/supersede`), { canonicalDeliveryId: keep, reason: reason.trim() });
    },
    onSuccess: refresh,
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not mark that duplicate.'),
  });
  const dismiss = useMutation({
    mutationFn: () => api.post(path(`/deliveries/${p.a}/not-duplicate`), { otherId: p.b, reason: reason.trim() }),
    onSuccess: refresh,
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not record that.'),
  });

  const dropRef = drop === 'a' ? p.aReference : p.bReference;
  const keepRef = drop === 'a' ? p.bReference : p.aReference;
  const asking = drop !== null || dismissing;

  return (
    <li className="space-y-2 py-3">
      <div className="space-y-0.5 text-sm">
        {p.reason === 'same_reference' && (
          <p className="text-xs font-medium text-amber-800">Same order number on both records</p>
        )}
        <div className="flex flex-wrap items-baseline gap-x-2">
          <Link to={`/deliveries/${p.a}`} className="font-mono text-xs text-indigo-700 hover:underline">
            {p.aReference}
          </Link>
          <span className="text-xs text-slate-500">{describeSide(p.first)}</span>
        </div>
        <div className="flex flex-wrap items-baseline gap-x-2">
          <Link to={`/deliveries/${p.b}`} className="font-mono text-xs text-indigo-700 hover:underline">
            {p.bReference}
          </Link>
          <span className="text-xs text-slate-500">{describeSide(p.second)}</span>
        </div>
        <p className="text-xs text-slate-500">
          {p.customerPhone ? `${p.customerPhone} · ` : ''}
          {fee}
        </p>
      </div>

      {error && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {asking ? (
        <div className="space-y-2">
          <Input
            label={dismissing ? 'Why are these two different jobs?' : `Why is ${dropRef} a duplicate of ${keepRef}?`}
            required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="flex gap-2">
            {dismissing ? (
              <Button onClick={() => dismiss.mutate()} loading={dismiss.isPending} disabled={reason.trim().length < 3}>
                Not a duplicate
              </Button>
            ) : (
              <Button onClick={() => supersede.mutate()} loading={supersede.isPending} disabled={reason.trim().length < 3}>
                Mark {dropRef} as duplicate
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => {
                setDrop(null);
                setDismissing(false);
              }}
            >
              Back
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => setDrop('b')}>
            Keep {p.aReference}
          </Button>
          <Button variant="secondary" onClick={() => setDrop('a')}>
            Keep {p.bReference}
          </Button>
          <Button variant="ghost" onClick={() => setDismissing(true)}>
            Not a duplicate
          </Button>
        </div>
      )}
    </li>
  );
}

interface FeeReviewRow {
  id: string;
  externalRef: string;
  orderRef: string | null;
  status: 'needs_review' | 'resolved';
  kind: 'fee_mismatch' | 'duplicate_reference';
  recordedAmount: string;
  verifiedAmount: string;
  difference: string;
}

/**
 * Counter records whose CENO order also arrived from WhatsApp with another
 * amount. Each opens the delivery, where an owner or admin records a decision.
 */
function FeeMismatches(): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();
  const currency = business?.currencyCode ?? 'NGN';
  const query = useQuery({
    queryKey: ['fee-reviews', business?.businessId],
    queryFn: () => api.get<{ data: FeeReviewRow[] }>(path('/deliveries/fee-reviews')),
    enabled: Boolean(business),
  });
  const open = (query.data?.data ?? []).filter((r) => r.status === 'needs_review');
  return (
    <Card className="mt-4" data-testid="fee-mismatches">
      <CardHeader
        title="Fee mismatches"
        subtitle="A counter record and a WhatsApp record for the same order disagree. Nothing is changed until you decide."
      />
      {query.isLoading && <Spinner label="Checking amounts" />}
      {query.isError && <ErrorState message="Could not load fee reviews." />}
      {query.data && open.length === 0 && <EmptyState title="Nothing to review" description="Every shared order matches." />}
      {open.length > 0 && (
        <ul className="divide-y divide-slate-100">
          {open.map((r) => (
            <li key={r.id} className="flex flex-wrap items-baseline gap-x-3 py-3 text-sm">
              <Link to={`/deliveries/${r.id}`} className="font-mono text-xs text-indigo-700 hover:underline">
                {r.orderRef ?? r.externalRef}
              </Link>
              <span className="font-medium text-red-800">
                {r.kind === 'fee_mismatch' ? 'Fee Mismatch — Needs Review' : 'Duplicate Reference — Needs Review'}
              </span>
              <span className="text-slate-600">
                counter {money(r.recordedAmount, currency)} · customer paid {money(r.verifiedAmount, currency)} · difference{' '}
                {money(r.difference, currency)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
